import { File, FileBarChart, FileSignature, FileSpreadsheet, FileText } from 'lucide-react';
import { store } from '../redux/store';
import _ from 'lodash';
import { messageType } from '../constants';

export const generatePassword = (length = 12) => {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-_=+[]{}|;:,.<>?';
  let password = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    password += characters[randomIndex];
  }
  return password;
};

export function generateReduxKeys(keys) {
  const localStorageKeys = Object.keys(localStorage);

  const initialState = {};

  const isValidJSON = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  for (const key of Object.keys(keys)) {
    const generatedKey = key;
    const initialValue = keys[key];

    const keysList = localStorageKeys.filter((key) => key.endsWith(generatedKey));
    if (!keysList.includes(generatedKey)) initialState[generatedKey] = initialValue;

    keysList.forEach((key) => {
      const value = localStorage.getItem(key);

      if (value && isValidJSON(value)) {
        initialState[key] = JSON.parse(value);
      } else {
        initialState[key] = initialValue;
      }
    });
  }

  return initialState;
}

export const getFullName = (user) => {
  const fullName = user?.first_name ? `${user?.first_name} ${user?.last_name ?? ''}` : user?.name;
  return fullName?.trim();
};

export const getInitials = (name) => {
  if (!name) return '';

  const nameParts = name
    .split(' ')
    .map((part) => part.trim())
    .filter((part) => /^[A-Za-z]/.test(part));

  if (nameParts.length === 0) return '';
  if (nameParts.length === 1) return nameParts[0][0]?.toUpperCase() || '';

  const firstInitial = nameParts[0][0]?.toUpperCase() || '';
  const lastInitial = nameParts[nameParts.length - 1][0]?.toUpperCase() || '';

  return firstInitial + lastInitial;
};

export const handleLogout = () => {
  const theme = localStorage.getItem('theme');
  localStorage.clear();
  if (theme) localStorage.setItem('theme', theme);
  window.location.href = '/';
};

export const getContactById = (id) => {
  const contacts = store.getState().contactsSlice.contacts;
  return contacts?.find((contact) => String(contact.id) === String(id));
};

export const scrollToBottom = (containerRef) => {
  if (containerRef?.current) {
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }
};

export const playNotifySound = (volume = 1, value) => {
  const { notification } = store.getState().preferenceSlice;
  const audio = new Audio(`/sounds/${value || notification || 'default'}.mp3`);
  audio.volume = volume;
  audio.play().catch((err) => {
    console.warn('Audio play failed:', err);
  });
};

export function getNewFileName(oldFileName) {
  const extension = oldFileName?.slice(oldFileName?.lastIndexOf('.'));
  const baseName = oldFileName?.slice(0, oldFileName?.lastIndexOf('.'));
  const randomString = Math.random().toString(36).substring(2, 12);
  return `${baseName}-${randomString}${extension}`;
}

export function extractOriginalFileName(modifiedFileName) {
  const extensionIndex = modifiedFileName?.lastIndexOf('.');
  const extension = modifiedFileName?.slice(extensionIndex);
  const randomStringPattern = /-\w{10}(?=\.\w+$)/;
  const baseName = modifiedFileName?.replace(randomStringPattern, '');
  return baseName;
}

export const getDocIcon = (attachment) => {
  const ext = attachment?.split('.').pop();
  const extension = ext?.toLowerCase();

  if (!extension) return <File size={30} color="var(--primary-color)" />;

  if (['pdf'].includes(extension)) return <FileSignature size={30} color="var(--primary-color)" />;

  if (['doc', 'docx'].includes(extension))
    return <FileText size={30} color="var(--primary-color)" />;

  if (['ppt', 'pptx'].includes(extension))
    return <FileBarChart size={30} color="var(--primary-color)" />;

  if (['xls', 'xlsx', 'csv'].includes(extension))
    return <FileSpreadsheet size={30} color="var(--primary-color)" />;

  return <File size={30} color="var(--primary-color)" />;
};

export const safeJsonParse = (input) => {
  try {
    return _.isString(input) ? JSON.parse(input) : input;
  } catch {
    return input;
  }
};

export const extractVariables = (text = '', type = 'text') => {
  const matches = text.match(/{{\d+}}/g) || [];
  const uniqueMatches = [...new Set(matches)];
  return uniqueMatches.map((match) => ({
    name: match,
    type,
  }));
};

export const getFormattedName = (name) => {
  if (!name) return null;
  return name
    ?.split('_')
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    ?.join(' ');
};

export const isChatContactExists = (contactId) => {
  const contacts = store.getState().contactsSlice?.chatContacts || [];
  return contacts.some(contact => String(contact.id) === String(contactId));
};

export const getLastMessageByType = (type, isReceived) => {
  const prefix = isReceived ? "Received" : "Sent";

  switch (type) {
    case messageType.IMAGE:
      return `${prefix} an Image`;

    case messageType.VIDEO:
      return `${prefix} a Video`;

    case messageType.DOCUMENT:
      return `${prefix} a Document`;

    case messageType.AUDIO:
      return `${prefix} an Audio`;

    case messageType.STICKER:
      return `${prefix} a Sticker`;

    case messageType.TEMPLATE:
      return `${prefix} a Template`;

    case messageType.TEXT:
      return isReceived ? "Received a message" : "Sent a message";

    default:
      return null;
  }
};

